import { z } from "zod/v4";
import { nullableUuid, orderTypeSchema } from "./basicTypes";
import { deltaFromObject, entityWithType } from "./EntityModel";

export { nullableUuid };

export const metricSeriesObjectSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  valueType: z.enum(["deci", "int"]),
});
export const metricSeriesEntitySchema = entityWithType(metricSeriesObjectSchema, "metric_series");
export const metricSeriesDeltaSchema = deltaFromObject(
  metricSeriesObjectSchema,
  "metric_series",
  [],
);

export const metricObjectSchema = z.object({
  id: z.uuid(),
  seriesId: z.uuid(),
  date: z.string(),
  value: z.string(),
});
export const metricEntitySchema = entityWithType(metricObjectSchema, "metric");
export const metricDeltaSchema = deltaFromObject(metricObjectSchema, "metric", ["date"]);

const componentsEnum = z.enum(["timebox", "condition", "goals", "pull", "project"]);

export type TaskComponentType = z.infer<typeof componentsEnum>;

export interface MetricSeries extends z.input<typeof metricSeriesEntitySchema> {}
export type MetricSeriesDelta = z.input<typeof metricSeriesDeltaSchema>;

export interface Metric extends z.input<typeof metricEntitySchema> {}
export type MetricDelta = z.input<typeof metricDeltaSchema>;

export const goalObjectSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export const goalEntitySchema = entityWithType(goalObjectSchema, "goal");
export const goalDeltaSchema = deltaFromObject(goalObjectSchema, "goal", []);

export type Goal = z.input<typeof goalEntitySchema>;
export type GoalDelta = z.input<typeof goalDeltaSchema>;

export const journalObjectSchema = z.object({
  id: z.uuid(),
  date: z.string(),
  content: z.array(z.any()),
});
export const journalEntitySchema = entityWithType(journalObjectSchema, "journal");
export const journalDeltaSchema = deltaFromObject(journalObjectSchema, "journal", ["date"]);

export type Journal = z.input<typeof journalEntitySchema>;
export type JournalDelta = z.input<typeof journalDeltaSchema>;

const baseTaskObjectSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  order: orderTypeSchema,
  isFolder: z.boolean(),
  content: z.any(),
  disabled: z.boolean(),
  condition: z.string().nullable(),
  startTime: z.iso.time({ precision: -1 }).nullable(),
  endTime: z.iso.time({ precision: -1 }).nullable(),
  completed: z.boolean(),
  parentId: z.uuid().nullable(),
  components: z.array(componentsEnum),
});

export const habitObjectSchema = baseTaskObjectSchema.extend({
  goalId: z.uuid().nullable(),
  goalOrder: orderTypeSchema.nullable(),
});
export const habitEntitySchema = entityWithType(habitObjectSchema, "habit");
export const habitDeltaSchema = deltaFromObject(habitObjectSchema, "habit", []);

export interface Habit extends z.input<typeof habitEntitySchema> {}
export type HabitDelta = z.input<typeof habitDeltaSchema>;

export const instanceOverrideObjectSchema = baseTaskObjectSchema.partial().extend({
  id: baseTaskObjectSchema.shape.id,
  templateId: z.uuid(),
  date: z.string(),
});
export const instanceOverrideEntitySchema = entityWithType(
  instanceOverrideObjectSchema,
  "instance",
);
export const instanceOverrideDeltaSchema = deltaFromObject(
  instanceOverrideObjectSchema,
  "instance",
  ["date", "templateId"],
);

export interface InstanceOverride extends z.input<typeof instanceOverrideEntitySchema> {}
export type InstanceOverrideDelta = z.input<typeof instanceOverrideDeltaSchema>;
export type Instance = Omit<Habit, "type"> & Pick<InstanceOverride, "type" | "templateId" | "date">;

export const taskObjectSchema = baseTaskObjectSchema.extend({
  /** Habit template ids (folders) this backlog task is shown under on the day plan. */
  folders: z.array(z.uuid()).default([]),
  goalId: z.uuid().nullable(),
  goalOrder: orderTypeSchema.nullable(),
  /** Synced flag; the task form keeps this aligned with the `project` component pill. */
  isProject: z.boolean().default(false),
});
export const taskEntitySchema = entityWithType(taskObjectSchema, "task");
export const taskDeltaSchema = deltaFromObject(taskObjectSchema, "task", []);

export interface Task extends z.input<typeof taskEntitySchema> {}
export type TaskDelta = z.input<typeof taskDeltaSchema>;

export const anyEntitySchema = z.discriminatedUnion("type", [
  metricSeriesEntitySchema,
  metricEntitySchema,
  goalEntitySchema,
  journalEntitySchema,
  habitEntitySchema,
  instanceOverrideEntitySchema,
  taskEntitySchema,
]);

export const anyEntityType = z.union(anyEntitySchema.options.map((schema) => schema.shape.type));

export const anyDeltaSchema = z.discriminatedUnion("type", [
  metricSeriesDeltaSchema,
  metricDeltaSchema,
  goalDeltaSchema,
  journalDeltaSchema,
  habitDeltaSchema,
  instanceOverrideDeltaSchema,
  taskDeltaSchema,
]);

export type AnyEntity = MetricSeries | Goal | Habit | InstanceOverride | Metric | Journal | Task;

export type AnyDelta =
  | MetricSeriesDelta
  | GoalDelta
  | HabitDelta
  | InstanceOverrideDelta
  | TaskDelta
  | JournalDelta
  | MetricDelta;
